import _ from "lodash";

import { IGenericModelFields } from "../IGenericFields";
import { WorkspaceResourceType } from "./IWorkspaceResource";
import { WorkspaceUserRole } from "./IWorkspace";
import { IWorkspacePermissionAccess } from "./IWorkspacePermission";

export enum WorkspaceResourceFolderType {
    /**
     * What used to be user folders
     */
    PERSONAL = "personal",
    /**
     * What used to be team folders
     */
    SHARED = "shared",
    /**
     * What used to be all team members folder
     */
    SHARED_DEFAULT = "sharedDefault"
}

export interface IWorkspaceResourceFolder extends IGenericModelFields {
    readonly workspaceId: string;
    readonly resourcesType: WorkspaceResourceType;
    folderType: WorkspaceResourceFolderType;
    name: string;
    userGroupId: string;
    permissionsOverrides?: {
        [key in WorkspaceUserRole]?: IWorkspacePermissionAccess;
    }
    parentFolderId?: string;
}

export const ALL_TEAM_MEMBERS_NAME = "All Team Members";
export const ALL_TEAM_MEMBERS_FOLDER_NAME = ALL_TEAM_MEMBERS_NAME;

export function isAllTeamMembersFolder(folder: Pick<IWorkspaceResourceFolder, "folderType">) {
    return folder.folderType === WorkspaceResourceFolderType.SHARED_DEFAULT;
}

export interface IWorkspaceResourceFolderMetadataCounters {
    users: {
        [key in WorkspaceUserRole]: number;
    },
    resources: {
        [key in WorkspaceResourceType]: number;
    }
}

export interface IWorkspaceResourceFolderMetadata {
    id: string;
    name: string;
    counters: IWorkspaceResourceFolderMetadataCounters;
}

////////////////////////////// DEFAULTS //////////////////////////////

const DEFAULT_METADATA_COUNTERS: IWorkspaceResourceFolderMetadataCounters = {
    users: Object.values(WorkspaceUserRole).reduce((acc, role) => ({ ...acc, [role]: 0 }), {} as { [key in WorkspaceUserRole]: number }),
    resources: Object.values(WorkspaceResourceType).reduce((acc, resourceType) => ({ ...acc, [resourceType]: 0 }), {} as { [key in WorkspaceResourceType]: number }),
};

export const GET_DEFAULT_WORKSPACE_RESOURCE_FOLDER_METADATA_COUNTERS = (): IWorkspaceResourceFolderMetadataCounters => _.cloneDeep(DEFAULT_METADATA_COUNTERS);

export function GET_DEFAULT_WORKSPACE_RESOURCE_FOLDER_PERMISSIONS_OVERRIDES(folderType: WorkspaceResourceFolderType): IWorkspaceResourceFolder["permissionsOverrides"] {
    if (folderType === WorkspaceResourceFolderType.SHARED_DEFAULT) {
        return {
            [WorkspaceUserRole.OWNER]: {
                read: {
                    object: true,
                    permissions: true
                },
                write: {
                    object: true,
                    permissions: true
                },
                owner: true
            },
            [WorkspaceUserRole.LIBRARIAN]: {
                read: {
                    object: true,
                    permissions: true
                },
                write: {
                    object: true,
                    permissions: false
                },
                owner: false
            },
            [WorkspaceUserRole.MEMBER]: {
                read: {
                    object: true,
                    permissions: true,
                },
                write: {
                    object: true,
                    permissions: false,
                },
                owner: false
            },
            [WorkspaceUserRole.GUEST]: {
                read: {
                    object: true,
                    permissions: true
                },
                write: {
                    object: false,
                    permissions: false
                },
                owner: false
            }
        };
    }

    return {
        [WorkspaceUserRole.OWNER]: {
            read: {
                object: true,
                permissions: true
            },
            write: {
                object: true,
                permissions: true
            },
            owner: true
        },
        [WorkspaceUserRole.LIBRARIAN]: {
            read: {
                object: true,
                permissions: true
            },
            write: {
                object: true,
                permissions: true
            },
            owner: false
        },
        [WorkspaceUserRole.MEMBER]: {
            read: {
                object: true,
                permissions: true
            },
            write: {
                object: true,
                permissions: true
            },
            owner: false
        },
        [WorkspaceUserRole.GUEST]: {
            read: {
                object: true,
                permissions: true
            },
            write: {
                object: false,
                permissions: false
            },
            owner: false
        }
    };
}
